import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CustomCalendar } from '@/shared/components/ui/custom-calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  PredefinedPeriod,
  SelectionMode,
  useDateRangePicker,
} from '@/shared/hooks/use-date-range-picker';
import { cn } from '@/shared/utils/cn';

type CustomDatePickerDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  startDate?: Date;
  endDate?: Date;
  onDateRangeSelect: (
    startDate: Date | undefined,
    endDate: Date | undefined,
  ) => void;
  title?: string;
  children?: React.ReactNode;
  showPredefinedPeriods?: boolean;
};

export const CustomDatePickerDialog = ({
  isOpen,
  onOpenChange,
  startDate,
  endDate,
  onDateRangeSelect,
  title = 'Zeitraum auswählen',
  children,
  showPredefinedPeriods = false,
}: CustomDatePickerDialogProps) => {
  const { t } = useTranslation('shared', { keyPrefix: 'DatePicker' });
  const {
    tempStartDate,
    tempEndDate,
    setTempStartDate,
    setTempEndDate,
    handleReset,
    handleApply,
    handleOpenChange,
    selectionMode,
    handleSelectionModeChange,
    selectedPeriod,
    handlePeriodSelect,
  } = useDateRangePicker({
    startDate,
    endDate,
    onDateRangeSelect,
    isOpen,
    onOpenChange,
    showPredefinedPeriods,
  });

  const getPeriodLabel = (period: PredefinedPeriod) => {
    switch (period) {
      case 'thisMonth':
        return t('thisMonth');
      case 'lastMonth':
        return t('lastMonth');
      case 'thisQuarter':
        return t('thisQuarter');
      case 'lastQuarter':
        return t('lastQuarter');
      case 'thisYear':
        return t('thisYear');
      case 'lastYear':
        return t('lastYear');
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={8}
        className="relative flex w-[360px] flex-col gap-4 rounded-[16px] p-4"
      >
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between px-2">
            <span className="text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-white">
              {title}
            </span>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex items-center justify-center rounded text-white/60 transition-colors hover:text-white"
            >
              <X className="size-4" />
            </button>
          </div>
          {/* Divider line */}
          <div className="h-[0.5px] w-full bg-white/20" />
        </div>

        {showPredefinedPeriods ? (
          <>
            {/* Radio Options */}
            <RadioGroup
              value={selectionMode}
              onValueChange={(value) =>
                handleSelectionModeChange(value as SelectionMode)
              }
              className="flex flex-col gap-3 py-2"
            >
              {/* Option 1: Heute */}
              <label
                htmlFor="mode-today"
                className="flex cursor-pointer items-center gap-3"
              >
                <RadioGroupItem value="today" id="mode-today" />
                <span className="text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-white">
                  {t('today')}
                </span>
              </label>

              {/* Option 2: Dropdown for predefined periods */}
              <div className="flex items-center gap-3">
                <RadioGroupItem value="predefined" id="mode-predefined" />
                <Select
                  value={selectedPeriod}
                  onValueChange={(value) =>
                    handlePeriodSelect(value as PredefinedPeriod)
                  }
                  disabled={selectionMode !== 'predefined'}
                >
                  <SelectTrigger
                    variant="transparent"
                    size="sm"
                    className={cn(
                      'flex-1 overflow-x-clip',
                      selectionMode !== 'predefined' && 'opacity-50',
                    )}
                  >
                    <SelectValue
                      asLabelInput={false}
                      placeholder={t('thisQuarter')}
                    >
                      {getPeriodLabel(selectedPeriod)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="thisMonth">{t('thisMonth')}</SelectItem>
                    <SelectItem value="lastMonth">{t('lastMonth')}</SelectItem>
                    <SelectItem value="thisQuarter">
                      {t('thisQuarter')}
                    </SelectItem>
                    <SelectItem value="lastQuarter">
                      {t('lastQuarter')}
                    </SelectItem>
                    <SelectItem value="thisYear">{t('thisYear')}</SelectItem>
                    <SelectItem value="lastYear">{t('lastYear')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Divider between dropdown and custom */}
              <div className="h-[0.5px] w-full bg-white/20" />

              {/* Option 3: Individueller Zeitraum */}
              <label
                htmlFor="mode-custom"
                className="flex cursor-pointer items-center gap-3"
              >
                <RadioGroupItem value="custom" id="mode-custom" />
                <span className="text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-white">
                  {t('customPeriod')}
                </span>
              </label>
            </RadioGroup>

            {/* Calendar - only visible when custom is selected */}
            {selectionMode === 'custom' && (
              <div className="mb-2 rounded-[8px] border-[0.5px] border-dashed border-white/30 bg-gradient-to-b from-white/0 to-white/[0.06] px-2 py-3 shadow-[0px_1px_0px_0px_rgba(0,0,0,0.05),0px_4px_4px_0px_rgba(0,0,0,0.05),0px_10px_10px_0px_rgba(0,0,0,0.1)]">
                <CustomCalendar
                  mode="range"
                  startDate={tempStartDate}
                  endDate={tempEndDate}
                  onStartSelect={setTempStartDate}
                  onEndSelect={setTempEndDate}
                />
              </div>
            )}
          </>
        ) : (
          /* Default mode without predefined periods - show only calendar */
          <div className="mb-2 mt-2 rounded-[8px] border-[0.5px] border-dashed border-white/30 bg-gradient-to-b from-white/0 to-white/[0.06] px-2 py-3 shadow-[0px_1px_0px_0px_rgba(0,0,0,0.05),0px_4px_4px_0px_rgba(0,0,0,0.05),0px_10px_10px_0px_rgba(0,0,0,0.1)]">
            <CustomCalendar
              mode="range"
              startDate={tempStartDate}
              endDate={tempEndDate}
              onStartSelect={setTempStartDate}
              onEndSelect={setTempEndDate}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleReset}
            className={cn(
              'relative flex h-9 flex-1 items-center justify-center rounded-[8px]',
              'border border-white/20 bg-transparent',
              'text-[13px] font-medium tracking-[-0.4px] text-white',
              'transition-all hover:border-white/30 hover:bg-white/5',
            )}
          >
            Zurücksetzen
          </button>
          <button
            type="button"
            onClick={handleApply}
            className={cn(
              'relative flex h-9 flex-1 items-center justify-center rounded-[8px]',
              'border-[0.5px] border-white',
              'text-[13px] font-medium tracking-[-0.4px] text-black',
              'transition-all hover:opacity-90',
            )}
            style={{
              backgroundImage:
                'linear-gradient(102.351deg, rgb(161, 255, 128) 0.16086%, rgb(113, 178, 90) 147.39%)',
            }}
          >
            Anwenden
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
